import { extendTheme } from '@chakra-ui/react';
import { PHONE_QUERY } from './hooks/useIsPhone';

// Theme rules that apply only when the phone layout does (see useIsPhone).
const PHONE_MEDIA = `@media screen and ${PHONE_QUERY}`;

// iOS Safari zooms into any field under 16px on focus and does not zoom back
// out. Below `md` every field is 16px; from `md` up it keeps its token.
const phoneFieldSize = (token) => ({
  field: {
    fontSize: { base: '16px', md: token },
    minH: { base: '44px', md: 'auto' },
  },
});

// Tappable at base: 44px is the smallest target a thumb hits reliably.
// From `md` up each size keeps Chakra's own minimum width, except on a phone
// held in landscape (`md` by width), where the phone block below sets
// `--mk-tap-min`. A variable rather than a media rule in the size, so an
// explicit minH/minW prop still wins, as it does at base.
const phoneButtonTarget = (minW) => ({
  minH: { base: '44px', md: 'var(--mk-tap-min, auto)' },
  minW: { base: '44px', md: `var(--mk-tap-min, var(--chakra-sizes-${minW}))` },
});

// Chakra's own parts drawn from the Paper and Night tokens, so every dialog,
// menu and field follows the colour mode without touching its screen. Passed
// to extendTheme after the main extension, which it deep-merges into.
const FIELD = {
  bg: 'redesign.bg.surface',
  borderColor: 'redesign.border.strong',
  _hover: { borderColor: 'redesign.text.muted' },
  _placeholder: { color: 'redesign.text.muted' },
  _focusVisible: {
    borderColor: 'redesign.accent.blue',
    boxShadow: '0 0 0 1px var(--chakra-colors-redesign-accent-blue)',
  },
  _invalid: {
    borderColor: 'redesign.accent.red',
    boxShadow: '0 0 0 1px var(--chakra-colors-redesign-accent-red)',
  },
};
const OVERLAY = { bg: 'rgba(26, 25, 23, 0.32)', _dark: { bg: 'rgba(0, 0, 0, 0.6)' } };
const POPUP_SHADOW = '0 8px 28px rgba(16, 24, 40, 0.12)';
const CHECKED = {
  bg: 'redesign.accent.fill',
  borderColor: 'redesign.accent.fill',
  color: 'white',
  _hover: { bg: '#1D4ED8', borderColor: '#1D4ED8' },
};

// What each Chakra colour scheme means in Paper and Night. A screen that asks
// for `colorScheme="green"` gets the app's green, not Chakra's.
const TINT = {
  green: { color: 'redesign.amount.income', bg: 'rgba(21, 128, 61, 0.10)', dark: 'rgba(74, 222, 128, 0.14)' },
  red: { color: 'redesign.accent.red', bg: 'rgba(185, 28, 28, 0.08)', dark: 'rgba(248, 113, 113, 0.14)' },
  orange: { color: 'redesign.text.warn', bg: 'rgba(180, 83, 9, 0.12)', dark: 'rgba(250, 204, 21, 0.14)' },
  yellow: { color: 'redesign.text.warn', bg: 'rgba(180, 83, 9, 0.12)', dark: 'rgba(250, 204, 21, 0.14)' },
  blue: { color: 'redesign.accent.blue', bg: 'redesign.bg.accent', dark: 'redesign.bg.accent' },
  purple: { color: 'redesign.accent.blue', bg: 'redesign.bg.accent', dark: 'redesign.bg.accent' },
  teal: { color: 'redesign.accent.blue', bg: 'redesign.bg.accent', dark: 'redesign.bg.accent' },
  gray: { color: 'redesign.text.muted', bg: 'redesign.bg.card', dark: 'redesign.bg.card' },
};
const tinted = (scheme) => {
  const t = TINT[scheme];
  return t ? { bg: t.bg, color: t.color, _dark: { bg: t.dark, color: t.color } } : {};
};
// An Alert's two colour variables. A token is named by its path; an rgba
// tint is used as it is.
const alertColor = (value) => (value && !value.startsWith('rgba') ? `colors.${value}` : value);
const alertVars = (fg, bg, bgDark) => (fg ? {
  '--alert-fg': alertColor(fg),
  '--alert-bg': alertColor(bg),
  _dark: { '--alert-fg': alertColor(fg), '--alert-bg': alertColor(bgDark) },
} : {});
// Filled buttons carry white text, so their fills are the shades that clear
// 4.5:1 under it in both modes.
const FILLED = {
  blue: ['redesign.accent.fill', '#1D4ED8', '#1E40AF'],
  red: ['#B91C1C', '#991B1B', '#7F1D1D'],
  green: ['#15803D', '#166534', '#14532D'],
};

const paperNightParts = {
  components: {
    Modal: {
      baseStyle: {
        overlay: OVERLAY,
        dialog: {
          bg: 'redesign.bg.surface',
          border: '1px solid',
          borderColor: 'redesign.border',
          borderRadius: '14px',
          boxShadow: '0 16px 48px rgba(16, 24, 40, 0.18)',
          _dark: { boxShadow: '0 16px 48px rgba(0, 0, 0, 0.55)' },
        },
        header: { color: 'redesign.heading' },
        closeButton: { color: 'redesign.text.muted' },
      },
    },
    Drawer: {
      baseStyle: {
        overlay: OVERLAY,
        dialog: { bg: 'redesign.bg.surface' },
        header: { color: 'redesign.heading' },
        closeButton: { color: 'redesign.text.muted' },
      },
    },
    Menu: {
      baseStyle: {
        list: {
          bg: 'redesign.bg.popup',
          borderColor: 'redesign.border',
          borderRadius: '10px',
          boxShadow: POPUP_SHADOW,
        },
        item: {
          bg: 'transparent',
          color: 'redesign.text',
          _hover: { bg: 'redesign.bg.hover', color: 'redesign.heading' },
          _focus: { bg: 'redesign.bg.hover', color: 'redesign.heading' },
        },
        groupTitle: { color: 'redesign.text.muted' },
        divider: { borderColor: 'redesign.border' },
      },
    },
    Popover: {
      baseStyle: {
        content: {
          '--popper-bg': 'var(--chakra-colors-redesign-bg-popup)',
          '--popper-arrow-bg': 'var(--chakra-colors-redesign-bg-popup)',
          '--popper-arrow-shadow-color': 'var(--chakra-colors-redesign-border)',
          bg: 'redesign.bg.popup',
          borderColor: 'redesign.border',
          boxShadow: POPUP_SHADOW,
          _dark: {
            '--popper-bg': 'var(--chakra-colors-redesign-bg-popup)',
            '--popper-arrow-bg': 'var(--chakra-colors-redesign-bg-popup)',
          },
        },
      },
    },
    // Ink on Paper, light on Night.
    Tooltip: {
      baseStyle: {
        '--tooltip-bg': 'var(--chakra-colors-redesign-heading)',
        '--popper-arrow-bg': 'var(--chakra-colors-redesign-heading)',
        color: 'redesign.bg.surface',
        borderRadius: '6px',
        _dark: {
          '--tooltip-bg': 'var(--chakra-colors-redesign-heading)',
          '--popper-arrow-bg': 'var(--chakra-colors-redesign-heading)',
          color: 'redesign.bg.surface',
        },
      },
    },
    Input: { variants: { outline: { field: FIELD } } },
    Select: { variants: { outline: { field: FIELD } } },
    NumberInput: { variants: { outline: { field: FIELD } } },
    Textarea: { variants: { outline: FIELD } },
    Checkbox: {
      baseStyle: { control: { borderColor: 'redesign.checkbox.border', _checked: CHECKED, _indeterminate: CHECKED } },
    },
    Radio: {
      baseStyle: { control: { borderColor: 'redesign.checkbox.border', _checked: CHECKED } },
    },
    Switch: {
      baseStyle: {
        track: { bg: 'redesign.border.strong', _checked: { bg: 'redesign.accent.fill' } },
      },
    },
    Tabs: {
      variants: {
        line: {
          tablist: { borderColor: 'redesign.border' },
          tab: {
            color: 'redesign.text.muted',
            _selected: { color: 'redesign.heading', borderColor: 'redesign.accent.blue' },
            _hover: { color: 'redesign.heading' },
          },
        },
      },
    },
    Table: {
      variants: {
        simple: {
          th: {
            color: 'redesign.text.muted',
            borderColor: 'redesign.border',
            letterSpacing: 'wide',
          },
          td: { borderColor: 'redesign.border' },
        },
      },
    },
    Divider: { baseStyle: { borderColor: 'redesign.border' } },
    // `colorScheme="blue"` is the app's primary action wherever a screen asks
    // for Chakra's solid button rather than the `primary` variant; red and
    // green keep their meaning on the app's own shades, gray is a quiet fill.
    Button: {
      variants: {
        solid: ({ colorScheme }) => {
          const f = FILLED[colorScheme];
          if (f) {
            return {
              bg: f[0],
              color: 'white',
              _hover: { bg: f[1], _disabled: { bg: f[0] } },
              _active: { bg: f[2] },
            };
          }
          return colorScheme === 'gray' ? {
            bg: 'redesign.bg.card',
            color: 'redesign.heading',
            _hover: { bg: 'redesign.bg.hover' },
            _active: { bg: 'redesign.bg.hover' },
          } : {};
        },
        outline: ({ colorScheme }) => (TINT[colorScheme] && colorScheme !== 'gray' ? {
          color: TINT[colorScheme].color,
          borderColor: 'currentColor',
          _hover: tinted(colorScheme),
        } : { borderColor: 'redesign.border.strong', color: 'redesign.text', _hover: { bg: 'redesign.bg.hover' } }),
        ghost: ({ colorScheme }) => (TINT[colorScheme] && colorScheme !== 'gray' ? {
          color: TINT[colorScheme].color,
          _hover: tinted(colorScheme),
        } : {}),
      },
    },
    Badge: {
      variants: {
        subtle: ({ colorScheme }) => tinted(colorScheme),
        outline: ({ colorScheme }) => (TINT[colorScheme] ? {
          color: TINT[colorScheme].color,
          boxShadow: 'inset 0 0 0px 1px currentColor',
        } : {}),
      },
    },
    Tag: {
      variants: {
        subtle: ({ colorScheme }) => ({ container: tinted(colorScheme) }),
      },
    },
    // Toasts are Chakra's solid alert: a popup with the status in its icon,
    // not a slab of colour. Inline alerts sit on their status tint. Chakra
    // paints the icon and the left accent from --alert-fg, the ground from
    // --alert-bg.
    Alert: {
      variants: {
        solid: ({ colorScheme }) => ({
          container: {
            ...alertVars(TINT[colorScheme]?.color, 'redesign.bg.popup', 'redesign.bg.popup'),
            color: 'redesign.heading',
            border: '1px solid',
            borderColor: 'redesign.border.strong',
            borderRadius: '10px',
            boxShadow: POPUP_SHADOW,
          },
          description: { color: 'redesign.text' },
        }),
        subtle: ({ colorScheme }) => ({
          container: alertVars(TINT[colorScheme]?.color, TINT[colorScheme]?.bg, TINT[colorScheme]?.dark),
        }),
        'left-accent': ({ colorScheme }) => ({
          container: alertVars(TINT[colorScheme]?.color, TINT[colorScheme]?.bg, TINT[colorScheme]?.dark),
        }),
      },
    },
  },
};

const theme = extendTheme({
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
  fonts: {
    heading: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  // The app's two styles, Paper (light) and Night (dark). Every screen reads
  // these tokens, so a value changed here changes every screen in both modes;
  // theme.contrast.test.js holds each text colour to 4.5:1 on every ground.
  //
  // Grounds stack from the window inwards: chrome (behind the sidebar and the
  // content panel) → page (the content panel) → surface (cards) → card and
  // sunken (fills inside a card).
  semanticTokens: {
    colors: {
      'redesign.bg.chrome':   { default: '#F3F1EC', _dark: '#05080E' },
      'redesign.bg.page':     { default: '#FBFAF8', _dark: '#0A0F19' },
      'redesign.bg.surface':  { default: '#FFFFFF', _dark: '#0E1421' },
      // A fill inside a card. At night it stays lighter than the card, the way
      // every existing use was drawn; `bg.sunken` is the darker one.
      'redesign.bg.card':     { default: '#F1EEE8', _dark: '#131B2B' },
      // Tracks, fields and segmented controls: below the card at night.
      'redesign.bg.sunken':   { default: '#F1EEE8', _dark: '#080C14' },
      // The chosen item: the active nav item, the active segment.
      'redesign.bg.raised':   { default: '#FFFFFF', _dark: '#172036' },
      'redesign.bg.hover':    { default: '#F5F3EF', _dark: '#121A29' },
      'redesign.bg.popup':    { default: '#FFFFFF', _dark: '#161E30' },
      'redesign.bg.accent':   { default: '#EAF1FE', _dark: '#13223D' },
      // Chat: the user's own message. A neutral one step off the page — not a
      // brand fill. `bg.card` is only 2–3 points from `bg.page` in light mode,
      // so the bubble would vanish; this is deliberately a touch deeper.
      'redesign.bg.bubble':   { default: '#F1EEE8', _dark: '#1B2333' },
      'redesign.border':        { default: '#E5E1D8', _dark: '#1A2232' },
      // Outlined controls and filter chips.
      'redesign.border.strong': { default: '#D6D0C4', _dark: '#273247' },
      // An empty checkbox's edge (MI-315): 3:1 on the surface it sits on.
      'redesign.checkbox.border': { default: '#948E85', _dark: '#6B7685' },
      'redesign.text':        { default: '#3D3B37', _dark: '#C9D1D9' },
      'redesign.text.muted':  { default: '#6B665D', _dark: '#8B98AA' },
      'redesign.text.faint':  { default: '#D4D0CA', _dark: '#3B4252' },
      // "Behind" written on its amber tint.
      'redesign.text.warn':   { default: '#8A5A00', _dark: '#FACC15' },
      'redesign.heading':     { default: '#1A1917', _dark: '#F4F6FA' },
      // Links, accents and focus, readable as text in both modes. A filled
      // button uses `accent.fill` instead: white on it is 5.2:1, where white on
      // the old #3B82F6 was 3.7:1.
      'redesign.accent.blue':    { default: '#1D4ED8', _dark: '#5B9BF3' },
      'redesign.accent.fill':    { default: '#2563EB', _dark: '#2563EB' },
      'redesign.accent.assets':  { default: '#3B82F6', _dark: '#5B9BF3' },
      'redesign.accent.expense': { default: '#EA6A3B', _dark: '#EF7547' },
      'redesign.accent.green':   { default: '#15803D', _dark: '#4ADE80' },
      'redesign.accent.amber':   { default: '#B45309', _dark: '#FACC15' },
      // A movement's review alert (the warning beside its name).
      'redesign.accent.alert':   { default: '#B93E0B', _dark: '#EF7547' },
      'redesign.accent.red':     { default: '#B91C1C', _dark: '#F87171' },
      'redesign.amount.income':  { default: '#157A3B', _dark: '#4ADE80' },
      // Money out is ink, not red: red is kept for something to fix.
      'redesign.amount.expense': { default: '#1A1917', _dark: '#F4F6FA' },
      // A transfer is neither in nor out.
      'redesign.amount.neutral': { default: '#6B665D', _dark: '#8B98AA' },
      'redesign.chart.grid':     { default: '#ECE8E0', _dark: '#172033' },
      // A chart's dashed comparison line: 3:1 on the card in each mode.
      'redesign.chart.previous': { default: '#8F887C', _dark: '#6B7685' },
      // A good-news tag, e.g. "120,000 under" on the spending trend.
      'redesign.chip.positive.bg': { default: '#15803D', _dark: '#4ADE80' },
      'redesign.chip.positive.fg': { default: '#FFFFFF', _dark: '#05080E' },
      // The environment tab (EnvBadge) on dev and local builds.
      'redesign.env.bg':   { default: '#B45309', _dark: '#FACC15' },
      'redesign.env.fg':   { default: '#FFFFFF', _dark: '#05080E' },
      // The phone layout's colours, on the same Paper and Night values as the
      // computer's. Each reaches 4.5:1 on the page, surface and card grounds in
      // its mode (theme.contrast.test.js).
      'phone.income':    { default: '#157A3B', _dark: '#4ADE80' },
      // Something wrong: overdue, failed, over pace. Money out is ink.
      'phone.expense':   { default: '#B91C1C', _dark: '#F87171' },
      // A step deeper than #C2410C, which is 4.47:1 on Paper's card fill.
      'phone.alert':     { default: '#B93E0B', _dark: '#EF7547' },
      'phone.muted':     { default: '#6B665D', _dark: '#8B98AA' },
      'phone.newPillBg': { default: '#DBEAFE', _dark: '#1E3A5F' },
      'phone.newPillFg': { default: '#1D4ED8', _dark: '#93C5FD' },
      'phone.badgeBg':   { default: '#EF7547', _dark: '#EF7547' },
      'phone.badgeFg':   { default: '#0B0F19', _dark: '#0B0F19' },
      'phone.link':      { default: '#1D4ED8', _dark: '#5B9BF3' },
      'phone.tabActive': { default: '#1D4ED8', _dark: '#5B9BF3' },
      // Something to fix: a tinted card or chip, its pressed state, and the
      // text on it (4.5:1 on the tint in each mode).
      'phone.alertBg':       { default: 'rgba(239, 117, 71, 0.12)', _dark: 'rgba(239, 117, 71, 0.12)' },
      'phone.alertBgActive': { default: 'rgba(239, 117, 71, 0.2)', _dark: 'rgba(239, 117, 71, 0.2)' },
      'phone.alertText':     { default: '#9A3412', _dark: '#F3B79E' },
      // A quiet pill on a card, e.g. the workspace role in the More sheet.
      'phone.chipBg':        { default: '#E8E5DE', _dark: '#2A3447' },
    },
    // Copilot's cards read as lifted rather than outlined: a hairline border
    // plus a shadow soft enough that you notice the lift, not the shadow. At
    // night a soft light shadow would glow, so the card sits on a dark one.
    shadows: {
      card: {
        default: '0 1px 2px rgba(16, 24, 40, 0.04), 0 1px 3px rgba(16, 24, 40, 0.06)',
        _dark: '0 0 0 1px rgba(0, 0, 0, 0.25), 0 8px 24px rgba(0, 0, 0, 0.28)',
      },
      cardHover: {
        default: '0 2px 4px rgba(16, 24, 40, 0.06), 0 4px 8px rgba(16, 24, 40, 0.06)',
        _dark: '0 0 0 1px rgba(0, 0, 0, 0.3), 0 10px 28px rgba(0, 0, 0, 0.34)',
      },
    },
  },
  // The one place numbers are typeset.
  //
  // Digits in a proportional font have different widths — a "1" is about half
  // the width of an "8" — so amounts of the same length render at different
  // widths and the decimal point lands somewhere new on every row. Tabular
  // figures are a second, fixed-width set of digits that fixes this.
  //
  // Inter ships them; DM Sans does not. Measured at 40px, "111111" vs "888888":
  // Inter with `tnum` gives 155.63px for both, while DM Sans gives 61.92px vs
  // 132.96px whether `tabular-nums` is set or not. That difference is the whole
  // reason figures used to be set in the system font — with Inter as the app
  // font they no longer need to leave it, so numbers and labels finally share
  // a typeface.
  textStyles: {
    tabularNumbers: {
      fontVariantNumeric: 'tabular-nums',
      fontFeatureSettings: '"tnum" 1',
    },
    // A card's lead figure. Its currency code is set by <Figure>, smaller and
    // raised, so the number carries the weight.
    figure: {
      fontSize: '34px', lineHeight: '40px', fontWeight: '600', letterSpacing: 'tighter',
      fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum" 1',
    },
    figureCode: { fontWeight: '600', letterSpacing: 'normal', color: 'redesign.text.muted' },
    // Day labels, column heads and "View all" links.
    microLabel: {
      fontSize: '11px', lineHeight: '14px', fontWeight: '600',
      textTransform: 'uppercase', letterSpacing: 'wide', color: 'redesign.text.muted',
    },
    // The phone scale, in px so it does not drift with the root size.
    phoneHero: {
      fontSize: '34px', lineHeight: '40px', fontWeight: '700', letterSpacing: 'tighter',
      fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum" 1',
    },
    phoneAmountInput: {
      fontSize: '40px', lineHeight: '48px', fontWeight: '700',
      fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum" 1',
    },
    phoneTitle: { fontSize: '20px', lineHeight: '26px', fontWeight: '700' },
    phoneHeadline: { fontSize: '17px', lineHeight: '22px', fontWeight: '600' },
    phoneBody: { fontSize: '16px', lineHeight: '22px' },
    phoneRowName: { fontSize: '16px', lineHeight: '22px', fontWeight: '500' },
    phoneRowAmount: {
      fontSize: '16px', lineHeight: '22px', fontWeight: '600',
      fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum" 1',
    },
    phoneMeta: { fontSize: '13px', lineHeight: '18px' },
    phoneCaption: {
      fontSize: '11px', lineHeight: '14px', fontWeight: '600',
      textTransform: 'uppercase', letterSpacing: 'wide',
    },
    // 10px below 360px, so "Movimientos" fits a 64px tab at 320.
    tabLabel: {
      fontSize: '11px', lineHeight: '13px', fontWeight: '500',
      '@media screen and (max-width: 359.98px)': { fontSize: '10px', lineHeight: '12px' },
    },
  },
  styles: {
    global: {
      html: {
        // 16px at every width. A smaller root on phones shrank every rem
        // token, fields included, and iOS zooms into any field under 16px.
        fontSize: '16px',
      },
      body: {
        fontSize: 'md',
        bg: 'redesign.bg.page',
        color: 'redesign.text',
        letterSpacing: 'tight',
      },
      // On a computer the content sits in a panel 8px in from the window's
      // top and bottom, and the panel scrolls. ProtectedLayout derives
      // --mk-page-h from it: the height a page that fills the screen takes
      // instead of 100vh.
      ':root': { '--mk-panel-h': 'calc(100vh - 16px)' },
      [PHONE_MEDIA]: {
        // The Button sizes' 44px minimum, for a landscape phone.
        ':root': { '--mk-tap-min': '44px' },
        // The net under the theme's field sizes: explicit fontSize props,
        // fields that inherit body text, and third-party inputs. A field meant
        // to be larger opts out with `data-display-size`.
        [[
          'input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([data-display-size])',
          'textarea:not([data-display-size])',
          'select:not([data-display-size])',
          '[contenteditable="true"]:not([data-display-size])',
        ].join(', ')]: {
          fontSize: '16px !important',
        },
        // Chakra sets the toast region's `bottom` inline. On the phone the
        // toasts sit 8px above the tab bar's raised + (16px above the bar,
        // less the 8px margin Chakra puts on each toast), so Undo is in thumb
        // reach and never covers it. A page with a different bottom bar
        // (onboarding, which has no tab bar) sets `--mk-toast-above` to its
        // height.
        '#chakra-toast-manager-bottom, #chakra-toast-manager-bottom-left, #chakra-toast-manager-bottom-right': {
          bottom: 'calc(var(--mk-toast-above, 49px) + env(safe-area-inset-bottom) + 16px) !important',
        },
        // A closable toast's ✕ is 24px; on the phone it fills a 44px corner
        // instead, and the text keeps clear of it.
        '[id^="chakra-toast-manager"] [id^="toast-"]:has(> button)': {
          paddingInlineEnd: '44px',
        },
        '[id^="chakra-toast-manager"] [id^="toast-"] > button': {
          top: 0,
          insetInlineEnd: 0,
          minWidth: '44px',
          minHeight: '44px',
        },
      },
    },
  },
  letterSpacings: {
    tighter: '-0.022em',  // large numbers and headings
    tight: '-0.011em',    // default for UI text
    normal: '0',
    wide: '0.06em',       // uppercase micro-labels
  },
  fontSizes: {
    '2xs': '0.625rem',  // 10px - badges, count chips
    xs: '0.6875rem',    // 11px - uppercase micro-labels
    sm: '0.75rem',      // 12px - secondary text
    md: '0.8125rem',    // 13px - default UI text
    lg: '0.9375rem',    // 15px
    xl: '1.0625rem',    // 17px
    '2xl': '1.25rem',   // 20px
    '3xl': '1.5rem',    // 24px
    '4xl': '1.875rem',  // 30px
  },
  space: {
    1: '0.25rem',  // 4
    2: '0.5rem',   // 8
    3: '0.75rem',  // 12
    4: '1rem',     // 16
    5: '1.25rem',  // 20
    6: '1.5rem',   // 24
    7: '1.75rem',  // 28
    8: '2rem',     // 32
  },
  components: {
    Table: {
      sizes: {
        sm: {
          td: { px: 3, py: 3, fontSize: 'sm' },
          th: { px: 3, py: 3, fontSize: 'sm', fontWeight: '600' },
        },
        md: {
          td: { px: 4, py: 4, fontSize: 'md' },
          th: { px: 4, py: 4, fontSize: 'md', fontWeight: '600' },
        },
      },
      defaultProps: {
        size: 'md',
      },
    },
    Button: {
      baseStyle: {
        fontWeight: '500',
      },
      sizes: {
        xs: {
          ...phoneButtonTarget('6'),
        },
        sm: {
          fontSize: 'sm',
          px: 4,
          py: 2,
          ...phoneButtonTarget('8'),
        },
        md: {
          fontSize: 'md',
          px: 5,
          py: 3,
          ...phoneButtonTarget('10'),
        },
      },
      variants: {
        // The phone's primary action: `primary`'s fill at the phone's size,
        // white on #2563EB at 5.2:1.
        phonePrimary: {
          bg: '#2563EB',
          color: 'white',
          h: '52px',
          borderRadius: '14px',
          fontSize: '17px',
          fontWeight: '600',
          _hover: { bg: '#1D4ED8', _disabled: { bg: '#2563EB' } },
          _active: { bg: '#1E40AF' },
        },
        // Filled in both modes: white on `accent.fill` is 5.2:1, so the night
        // no longer needs an outlined stand-in.
        primary: {
          bg: 'redesign.accent.fill',
          color: 'white',
          _hover: { bg: '#1D4ED8', _disabled: { bg: 'redesign.accent.fill' } },
          _active: { bg: '#1E40AF' },
        },
        danger: {
          bg: 'redesign.accent.red',
          color: 'white',
          _hover: { bg: '#991B1B' },
          _dark: {
            bg: 'transparent',
            border: '1px solid',
            borderColor: 'redesign.accent.red',
            color: 'redesign.accent.red',
            _hover: { bg: 'rgba(248, 113, 113, 0.10)' },
          },
        },
      },
      defaultProps: {
        size: 'md',
      },
    },
    Input: {
      sizes: {
        xs: phoneFieldSize('xs'),
        sm: {
          field: {
            ...phoneFieldSize('sm').field,
            px: 3,
            py: 2,
            height: '32px',
          },
        },
        md: {
          field: {
            ...phoneFieldSize('md').field,
            px: 4,
            py: 3,
            height: '36px',
          },
        },
        lg: phoneFieldSize('lg'),
      },
      defaultProps: {
        size: 'md',
      },
    },
    Select: {
      sizes: {
        xs: phoneFieldSize('xs'),
        sm: phoneFieldSize('sm'),
        md: {
          field: {
            ...phoneFieldSize('md').field,
            height: '36px',
          },
        },
        lg: phoneFieldSize('lg'),
      },
      defaultProps: {
        size: 'md',
      },
    },
    // Chakra builds these from its own input theme, so the Input override
    // above does not reach them.
    Textarea: {
      sizes: {
        xs: { fontSize: { base: '16px', md: 'xs' } },
        sm: { fontSize: { base: '16px', md: 'sm' } },
        md: { fontSize: { base: '16px', md: 'md' } },
        lg: { fontSize: { base: '16px', md: 'lg' } },
      },
    },
    // Textarea's own minimum (80px) is already past 44px.
    NumberInput: {
      sizes: {
        xs: phoneFieldSize('xs'),
        sm: phoneFieldSize('sm'),
        md: phoneFieldSize('md'),
        lg: phoneFieldSize('lg'),
      },
    },
    Tag: {
      sizes: {
        sm: {
          container: {
            fontSize: 'xs',
            px: 2,
            py: 1,
            minH: '24px',
          },
        },
        md: {
          container: {
            fontSize: 'sm',
            px: 3,
            py: 1,
            minH: '28px',
          },
        },
      },
      defaultProps: {
        size: 'md',
      },
    },
    Badge: {
      baseStyle: {
        fontSize: 'xs',
        px: 2,
        py: 1,
      },
    },
    Text: {
      // Chakra lets a component's baseStyle size win over the size inside a
      // `textStyle`, so a text style that sets its own size gets no default.
      baseStyle: ({ theme, textStyle }) => (
        theme.textStyles?.[textStyle]?.fontSize ? {} : { fontSize: 'md' }
      ),
    },
    Heading: {
      baseStyle: {
        letterSpacing: 'tighter',
      },
      sizes: {
        sm: { fontSize: 'lg' },
        md: { fontSize: 'xl' },
        lg: { fontSize: '2xl' },
        xl: { fontSize: '3xl' },
      },
    },
    IconButton: {
      sizes: {
        sm: {
          fontSize: 'md',
          w: '28px',
          h: '28px',
        },
        md: {
          fontSize: 'lg',
          w: '34px',
          h: '34px',
        },
      },
      defaultProps: {
        size: 'md',
      },
    },
    Menu: {
      baseStyle: {
        item: {
          fontSize: 'md',
          py: 3,
          px: 4,
        },
        list: {
          py: 2,
        },
      },
    },
    Modal: {
      baseStyle: {
        header: {
          fontSize: 'xl',
          fontWeight: '600',
        },
        body: {
          fontSize: 'md',
        },
      },
    },
    FormLabel: {
      baseStyle: {
        fontSize: 'md',
        fontWeight: '500',
        mb: 2,
      },
    },
    Tabs: {
      sizes: {
        md: {
          tab: {
            fontSize: 'md',
            py: 3,
            px: 4,
          },
        },
      },
    },
  },
}, paperNightParts);

// Touch browsers keep :hover on whatever was tapped last, so a ghost button
// (a sheet's ✕ or ⋯) kept a grey disc after the tap. Its hover fill now
// applies only where there is a hover; the pressed fill stays everywhere.
const chakraGhost = theme.components.Button.variants.ghost;
theme.components.Button.variants.ghost = (props) => {
  const style = chakraGhost(props);
  return style?._hover ? { ...style, _hover: { '@media (hover: hover)': style._hover } } : style;
};

export default theme;
